import { container, events } from '@game-studio/arcade-base';
import { IMediaInstance } from '@pixi/sound';
import gsap from 'gsap';
import { GameModel, gameModel } from '../models/game-model';
import { gameSettings } from '../models/game-settings';
import { collectorService } from './collector-service';

// Constants for volume settings.
enum VolumeSettings {
    Hum = 0.12, // Volume for hum sound.
    Piano = 0.04, // Volume for piano sound.
    Heart = 0.2 // Volume for heart sound.
}

// Configuration for audio fade durations.
const AudioFadeDuration = 2; // Duration for audio fade transitions (in seconds).

export class SoundService {
    private static _instance: SoundService;
    private _hum: IMediaInstance;
    private _piano: IMediaInstance;
    private _heart: IMediaInstance;

    private constructor() {
        // Add event listeners for game events.
        events.GAME.BEGIN.addListener(this.musicInGame, this);
        events.GAME.COMPLETE.addListener(() => {
            this.musicOutGame();

            // Check if a normal win sound should be played.
            const returnStake: number = container.stake.currentStake * gameSettings.winMultipliers[1];
            if (gameModel.payout > returnStake && gameModel.goldPrize === -1) {
                container.soundManager.sfx.play('normal-win', { volume: 0.4 });
            }
        });
    }

    public static get Instance(): SoundService {
        return this._instance || (this._instance = new SoundService());
    }

    public beginMusic(): void {
        // Initialize music and audio elements.
        this._hum = container.soundManager.music.play('hum', { volume: 0 });
        this._piano = container.soundManager.music.play('piano', { volume: 0 });
        this._heart = container.soundManager.music.play('heart', { volume: 0 });

        // Play introductory music track and fade it out after a delay.
        const IntroMusicFadeDelay = 5000; // Delay before fading out intro music (in milliseconds).
        const track = container.soundManager.music.play('music-intro', {
            loop: false,
            volume: 0.8,
            complete: () => {
                this.playMusicLoop();
            }
        });
        setTimeout(() => {
            gsap.to(track, { duration: AudioFadeDuration, volume: 0 });
        }, IntroMusicFadeDelay);
    }

    public playMusicLoop(): void {
        this.fadeIn(this._hum, VolumeSettings.Hum);
    }

    public musicInGame(): void {
        gsap.killTweensOf(this._piano);
        this._piano.volume = VolumeSettings.Piano;
    }

    public checkOneToGoMusic(): void {
        // Check there are any one to go states active.
        if (collectorService.toGoCount(gameModel.oneToGoStates) < GameModel.NUMBER_OF_COLLECTORS) {
            gsap.killTweensOf(this._heart);
            this._heart.volume = VolumeSettings.Heart;
            return;
        }
        // Kill heart beat when no states are active.
        gsap.killTweensOf(this._heart);
        this._heart.volume = 0.0;
    }

    public musicOutGame(): void {
        // Fade out music for end of the game
        this.fadeOut(this._heart);
        this.fadeOut(this._piano);
    }

    public playSymbolReveal(): void {
        container.soundManager.sfx.play('symbol-reveal');
    }

    public playGoldenReveal(): void {
        container.soundManager.sfx.play('golden-reveal');

        // Play a special sound effect and fade in the background music.
        const GoldWinSoundDelay = 1200; // Delay before playing gold win sound (in milliseconds).
        setTimeout(() => {
            const GoldWinVolume = VolumeSettings.Hum; // Volume for gold win sound.
            container.soundManager.sfx.play('gold-win', {
                complete: () => {
                    this.fadeIn(this._hum, GoldWinVolume);
                }
            });
        }, GoldWinSoundDelay);
    }

    public playInstantPrizeBegin(): void {
        container.soundManager.sfx.play('special-begin');
    }

    // Play sound effects and suspend music for an instant prize alert.
    public playInstantPrizeAlert(): void {
        this.suspendMusic();
        container.soundManager.sfx.play('special-alert');
        const InstantPrizeAlertDelay = 2700; // Delay before playing the second instant prize alert (in milliseconds).
        setTimeout(() => {
            container.soundManager.sfx.play('special-alert');
        }, InstantPrizeAlertDelay);
    }

    public playInstantPrizeComplete(): void {
        container.soundManager.sfx.play('special-complete');
    }

    public playWinSound(panelId: number): void {
        const superPrize: number = 6;
        const highPrize: number = 4;
        if (panelId === superPrize) {
            container.soundManager.sfx.play('super-win');
            return;
        }
        if (panelId >= highPrize) {
            container.soundManager.sfx.play('high-win');
            return;
        }
        container.soundManager.sfx.play('low-win');
    }

    public suspendMusic(): void {
        // Mute current dynamic music track.
        gsap.killTweensOf(this._hum);
        gsap.killTweensOf(this._piano);
        gsap.killTweensOf(this._heart);
        this._hum.volume = 0;
        this._piano.volume = 0;
        this._heart.volume = 0;
    }

    // Utility function to smoothly fade in audio.
    private fadeIn(track: IMediaInstance, volume: number): void {
        if (track) {
            gsap.to(track, { duration: AudioFadeDuration, volume: volume });
        }
    }

    // Utility function to smoothly fade out audio.
    private fadeOut(track: IMediaInstance): void {
        if (track) {
            gsap.to(track, { duration: AudioFadeDuration, volume: 0 });
        }
    }
}

export const soundService = SoundService.Instance;
